import { useEffect, useState } from 'react';

import { SkeletonNoLoginComponent } from 'commons/components';
import { PocProgressSpinner } from 'commons/components/components-wrapper';
import { env } from 'core/constants/env.constants';
import { useCustomFields } from 'core/contexts/custom-fields/use-custom-fields.hook';
import { useUserPreferencesContext } from 'core/contexts/user-preferences/hooks/use-user-preferences-context.hook';
import { useConsentUpdateTracking } from 'core/custom-hooks/use-consent-update-tracking.hook';
import { usePageTracking } from 'core/custom-hooks/use-page-tracking.hook';
import { StencilRouteTransitionContainer } from 'core/stencil';
import { useIsMount } from 'core/utils/useIsMount';
import { HeaderAppContainer } from 'modules/global/pods/header-app/app-header.container';
import { OperatorDetail } from 'modules/operators/domain';
import { Helmet } from 'react-helmet';
import { Outlet } from 'react-router-dom';
import { APP_HEADER_HEIGHT, APP_ROUTE_TRANSITION_BACKGROUND, APP_SIDE_MENU_WIDTH } from './app.layout.constants';
import { LayoutMainComponent } from './app.layout.style';
import { SideMenuComponent } from './menu';

export const AppLayout: React.FC<React.PropsWithChildren> = () => {
    const {
        userPreferencesId,
        sideMenuEntries,
        isLoading: isUserPreferencesLoading,
        refreshUserPreferences: updateUserPreferences,
        setSideMenuEntries,
    } = useUserPreferencesContext();
    const isMounted = useIsMount();
    const { refreshCustomFields } = useCustomFields();

    useEffect(() => {
        refreshCustomFields();
    }, []);

    // To track pages in adobe analytics
    usePageTracking();
    useConsentUpdateTracking();

    // CONTEXT FOR SIDE_SHEET
    const [operatorToShow, setOperatorToShow] = useState<OperatorDetail>();
    const [operatorToShowLoading, setOperatorToShowLoading] = useState<boolean>(false);
    const [showDrawer, setShowDrawer] = useState<boolean>(false);
    const [operatorFormId, setOperatorFormId] = useState<string | undefined>(undefined);
    const [refreshScreen, setRefreshScreen] = useState<() => VoidFunction>(() => () => {});
    // END OF CONTEXT FOR SIDE_SHEET

    // CONTEXT FOR ADM-DEVICES-LIST
    const [deviceFormId, setDeviceFormId] = useState<string | undefined>(undefined);
    // END OF CONTEXT FOR ADM-DEVICES-LIST

    if (isMounted && isUserPreferencesLoading) {
        return <SkeletonNoLoginComponent />;
    }
    return (
        isMounted && (
            <>
                <Helmet>
                    {env.VITE_ONE_TRUST_ID ? (
                        <script
                            type="text/javascript"
                            src={`https://cdn.cookielaw.org/consent/${env.VITE_ONE_TRUST_ID}/OtAutoBlock.js`}
                        ></script>
                    ) : null}
                    {env.VITE_ONE_TRUST_ID ? (
                        <script
                            src="https://cdn.cookielaw.org/scripttemplates/otSDKStub.js"
                            data-document-language="true"
                            type="text/javascript"
                            data-domain-script={env.VITE_ONE_TRUST_ID}
                        ></script>
                    ) : null}
                    {env.VITE_ADOBE_ANALYTICS_URL ? <script src={env.VITE_ADOBE_ANALYTICS_URL} /> : null}
                </Helmet>
                <HeaderAppContainer />
                <LayoutMainComponent>
                    <SideMenuComponent />
                    <Outlet
                        context={{
                            userPreferencesId,
                            sideMenuEntries,
                            setSideMenuEntries,
                            setSideMenuEntriesHasChanged: () => updateUserPreferences(),
                            operatorToShow,
                            setOperatorToShow,
                            showDrawer,
                            setShowDrawer,
                            operatorToShowLoading,
                            setOperatorToShowLoading,
                            refreshScreen,
                            setRefreshScreen,
                            operatorFormId,
                            setOperatorFormId,
                            deviceFormId,
                            setDeviceFormId,
                        }}
                    />
                </LayoutMainComponent>
                <StencilRouteTransitionContainer
                    backgroundColor={APP_ROUTE_TRANSITION_BACKGROUND}
                    leftOffset={APP_SIDE_MENU_WIDTH}
                    topOffset={APP_HEADER_HEIGHT}
                >
                    <PocProgressSpinner variant="primary" size="large" />
                </StencilRouteTransitionContainer>
            </>
        )
    );
};
